import { Link } from "react-router-dom";
import { ArrowRight, Camera, Calendar, Briefcase, Star, MapPin, Instagram } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { BRAND, IMAGES, TESTIMONIALS, GOOGLE } from "@/data";

const Home = () => {
  return (
    <div data-testid="home-page" className="bg-[#0c0c0c]">
      <Seo
        title="Salvatore Castelli Photography | Fotografo Matrimoni ed Eventi · Tuscia Viterbese e Roma"
        description="Fotografo di matrimoni, compleanni ed eventi nella Tuscia Viterbese e a Roma. Studio fotografico con base a Faleria (Viterbo). Reportage emozionale foto e video. Valutazione 5.0 su Google."
        path="/"
      />
      {/* HERO */}
      <section className="relative min-h-screen flex items-end" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={IMAGES.hero} alt="Matrimonio nella Tuscia" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0c0c0c] via-[#0c0c0c]/50 to-[#0c0c0c]/30" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pb-24 pt-32 w-full">
          <Reveal>
            <span className="text-sm uppercase tracking-[0.3em] text-gold">
              Fotografo · Tuscia Viterbese & Roma
            </span>
          </Reveal>
          <Reveal delay={120}>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl text-white font-light leading-[1.05] mt-6 max-w-4xl">
              Catturo i tuoi momenti più belli tra la <em className="text-gold not-italic">Tuscia Viterbese</em> e <em className="text-gold not-italic">Roma</em>
            </h1>
          </Reveal>
          <Reveal delay={240}>
            <p className="text-stone-300 text-base md:text-lg mt-8 max-w-xl leading-relaxed">
              Matrimoni, compleanni ed eventi raccontati con uno stile emozionale e senza tempo.
              Studio fotografico con base a Faleria, Viterbo.
            </p>
          </Reveal>
          <Reveal delay={360}>
            <div className="flex flex-col sm:flex-row gap-4 mt-10">
              <Link
                to="/contatti"
                data-testid="hero-cta-button"
                className="group bg-gold text-[#0c0c0c] uppercase tracking-[0.15em] text-sm font-medium px-8 py-4 hover:bg-[#b5952f] transition-colors duration-300 flex items-center justify-center gap-2"
              >
                Prenota una Consulenza Gratuita
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
              <Link
                to="/servizi"
                className="border border-stone-600 text-white uppercase tracking-[0.15em] text-sm px-8 py-4 hover:border-gold hover:text-gold transition-colors duration-300 flex items-center justify-center"
              >
                Scopri i Pacchetti
              </Link>
            </div>
          </Reveal>
          <Reveal delay={480}>
            <a
              href={GOOGLE.url}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-google-link"
              className="inline-flex items-center gap-2 mt-8 text-stone-300 hover:text-gold transition-colors duration-300"
            >
              <span className="flex gap-0.5 text-gold">
                {[...Array(5)].map((_, s) => <Star key={s} size={14} fill="#d4af37" />)}
              </span>
              <span className="text-sm">{GOOGLE.rating} · {GOOGLE.count} recensioni su Google</span>
            </a>
          </Reveal>
        </div>
      </section>

      {/* CHI SONO */}
      <section className="relative z-10 py-24 md:py-32" data-testid="about-preview">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <Reveal className="order-2 lg:order-1">
            <span className="text-sm uppercase tracking-[0.3em] text-gold">Chi Sono</span>
            <h2 className="font-serif text-3xl md:text-4xl text-white font-light mt-5 leading-tight">
              Un racconto autentico, un istante alla volta
            </h2>
            <p className="text-stone-400 leading-relaxed mt-6">
              Sono Salvatore Castelli, fotografo con base a Faleria. Da anni racconto le storie
              delle famiglie e delle coppie della Tuscia Viterbese e di Roma, cercando l&apos;emozione vera
              dietro ogni sorriso, ogni lacrima, ogni brindisi.
            </p>
            <p className="text-stone-400 leading-relaxed mt-4">
              Il mio approccio è discreto e naturale: non pose forzate, ma momenti che restano.
              Dal reportage di matrimonio alla festa di compleanno, fino ai progetti fotografici
              che seguono la crescita dei tuoi figli nel tempo.
            </p>
            <Link
              to="/chi-sono"
              data-testid="about-more-link"
              className="inline-flex items-center gap-2 text-gold uppercase tracking-[0.15em] text-sm mt-8 hover:gap-3 transition-all duration-300"
            >
              La mia storia <ArrowRight size={16} />
            </Link>
          </Reveal>
          <Reveal className="order-1 lg:order-2" delay={150}>
            <div className="overflow-hidden">
              <img src={IMAGES.portrait} alt="Salvatore Castelli fotografo" className="w-full h-[520px] object-cover hover:scale-105 transition-transform duration-700" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* SERVIZI IN EVIDENZA - BENTO */}
      <section className="relative z-10 py-24 md:py-32 bg-[#0a0a0a]" data-testid="services-preview">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal className="max-w-2xl">
            <span className="text-sm uppercase tracking-[0.3em] text-gold">Servizi</span>
            <h2 className="font-serif text-3xl md:text-4xl text-white font-light mt-5">
              Servizi fotografici in evidenza
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mt-14">
            <Reveal className="md:col-span-8 group relative overflow-hidden min-h-[360px]">
              <img src={IMAGES.wedding} alt="Fotografo matrimoni Tuscia" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0c0c0c] via-[#0c0c0c]/30 to-transparent" />
              <div className="relative z-10 h-full flex flex-col justify-end p-8">
                <Camera className="text-gold mb-3" size={26} />
                <h3 className="font-serif text-2xl md:text-3xl text-white">Matrimoni & Wedding Film</h3>
                <p className="text-stone-300 text-sm mt-2 max-w-md">
                  Reportage emozionale foto e video, dalla vestizione al ricevimento. Fotografo matrimoni nella Tuscia Viterbese e a Roma.
                </p>
              </div>
            </Reveal>

            <Reveal className="md:col-span-4 group relative overflow-hidden min-h-[360px]" delay={120}>
              <img src={IMAGES.birthday} alt="Fotografo compleanni Viterbo" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0c0c0c] via-[#0c0c0c]/30 to-transparent" />
              <div className="relative z-10 h-full flex flex-col justify-end p-8">
                <Calendar className="text-gold mb-3" size={26} />
                <h3 className="font-serif text-2xl text-white">Compleanni & Eventi</h3>
                <p className="text-stone-300 text-sm mt-2">
                  Servizi rapidi e dinamici, consegna in soli 10 giorni.
                </p>
              </div>
            </Reveal>

            <Reveal className="md:col-span-12 group relative overflow-hidden border border-gold/40 bg-[#111]" delay={200}>
              <div className="relative z-10 p-8 md:p-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="max-w-2xl">
                  <div className="flex items-center gap-3 mb-3">
                    <Briefcase className="text-gold" size={24} />
                    <span className="text-xs uppercase tracking-[0.25em] text-gold">Business</span>
                  </div>
                  <h3 className="font-serif text-2xl md:text-3xl text-white">Foto & Video Aziendali</h3>
                  <p className="text-stone-400 text-sm mt-3">
                    Servizi fotografici e video professionali per aziende: eventi e convention, ritratti
                    corporate del team e contenuti per social e sito web.
                  </p>
                </div>
                <Link
                  to="/servizi"
                  data-testid="services-aziende-link"
                  className="shrink-0 bg-gold text-[#0c0c0c] uppercase tracking-[0.15em] text-sm font-medium px-7 py-4 hover:bg-[#b5952f] transition-colors duration-300 flex items-center gap-2"
                >
                  Scopri di più <ArrowRight size={16} />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PORTFOLIO / INSTAGRAM GRID */}
      <section className="relative z-10 py-24 md:py-32" data-testid="portfolio-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <span className="text-sm uppercase tracking-[0.3em] text-gold">Portfolio</span>
              <h2 className="font-serif text-3xl md:text-4xl text-white font-light mt-5">
                Gli ultimi scatti
              </h2>
            </div>
            <a
              href={BRAND.instagram}
              target="_blank"
              rel="noreferrer"
              data-testid="portfolio-instagram-link"
              className="inline-flex items-center gap-2 text-stone-300 hover:text-gold transition-colors uppercase tracking-[0.15em] text-sm"
            >
              <Instagram size={18} /> Seguimi su {BRAND.instagramHandle}
            </a>
          </Reveal>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-1 px-1">
          {IMAGES.gallery.map((src, i) => (
            <div key={i} className="group relative overflow-hidden aspect-square" data-testid={`portfolio-item-${i}`}>
              <img src={src} alt={`Portfolio matrimoni ed eventi Tuscia ${i + 1}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
              <div className="absolute inset-0 bg-[#0c0c0c]/0 group-hover:bg-[#0c0c0c]/40 transition-colors duration-500 flex items-center justify-center">
                <Instagram className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-500" size={26} />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="relative z-10 py-24 md:py-32 bg-[#0a0a0a]" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal className="max-w-2xl mb-14">
            <span className="text-sm uppercase tracking-[0.3em] text-gold">Recensioni</span>
            <h2 className="font-serif text-3xl md:text-4xl text-white font-light mt-5">
              La fiducia dei miei clienti
            </h2>
            <a
              href={GOOGLE.url}
              target="_blank"
              rel="noreferrer"
              data-testid="google-rating-badge"
              className="inline-flex items-center gap-3 mt-6 border border-stone-800 bg-[#111] px-5 py-3 hover:border-gold transition-colors duration-300"
            >
              <span className="font-serif text-2xl text-gold">{GOOGLE.rating}</span>
              <span className="flex gap-0.5 text-gold">
                {[...Array(5)].map((_, s) => <Star key={s} size={15} fill="#d4af37" />)}
              </span>
              <span className="text-sm text-stone-300">{GOOGLE.count} recensioni su Google</span>
            </a>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={i} delay={i * 100} className="border border-stone-800 bg-[#111] p-8" data-testid={`testimonial-${i}`}>
                <div className="flex gap-1 text-gold mb-4">
                  {[...Array(5)].map((_, s) => <Star key={s} size={16} fill="#d4af37" />)}
                </div>
                <p className="text-stone-300 leading-relaxed italic">“{t.text}”</p>
                <div className="mt-6">
                  <div className="text-white font-medium">{t.name}</div>
                  <div className="text-xs uppercase tracking-widest text-stone-500 mt-1">{t.role}</div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-12" delay={200}>
            <a
              href={GOOGLE.url}
              target="_blank"
              rel="noreferrer"
              data-testid="leave-review-button"
              className="inline-flex items-center gap-2 border border-stone-600 text-white uppercase tracking-[0.15em] text-sm px-8 py-4 hover:border-gold hover:text-gold transition-colors duration-300"
            >
              <Star size={16} /> Lascia una recensione
            </a>
          </Reveal>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="relative z-10 py-24 md:py-28" data-testid="home-cta-band">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <MapPin className="text-gold mx-auto mb-6" size={30} />
            <h2 className="font-serif text-3xl md:text-5xl text-white font-light leading-tight">
              Attivo nella Tuscia Viterbese e a Roma
            </h2>
            <p className="text-stone-400 mt-6 max-w-xl mx-auto">
              Faleria · Viterbo e i borghi della Tuscia · Roma. Raccontiamo insieme il tuo prossimo evento.
            </p>
            <Link
              to="/contatti"
              data-testid="home-cta-final"
              className="inline-flex items-center gap-2 bg-gold text-[#0c0c0c] uppercase tracking-[0.15em] text-sm font-medium px-9 py-4 mt-10 hover:bg-[#b5952f] transition-colors duration-300"
            >
              Prenota una Consulenza Gratuita <ArrowRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
